import React, { useState, useEffect } from 'react';
import { adminFetchAllUsers, adminFetchAllCompetitions } from '../services/api';
import { Users, Trophy, Shield, Globe, Lock, ExternalLink, Calendar, Mail, User as UserIcon } from 'lucide-react';
import Swal from 'sweetalert2';

export default function AdminPanel({ onSelectCompetition }) {
  const [activeSubTab, setActiveSubTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, compsData] = await Promise.all([
        adminFetchAllUsers(),
        adminFetchAllCompetitions()
      ]);
      setUsers(usersData || []);
      setCompetitions(compsData || []);
    } catch (error) {
      console.error("Erreur admin loadData:", error);
      Swal.fire('Erreur', "Impossible de charger les données d'administration", 'error');
    } finally {
      setLoading(false);
    }
  };

  const tableHeaderStyle = {
    textAlign: 'left',
    padding: '15px',
    backgroundColor: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontSize: '13px',
    textTransform: 'uppercase',
    fontWeight: 'bold'
  };

  const tableCellStyle = {
    padding: '15px',
    borderBottom: '1px solid #edf2f7',
    color: '#2d3748',
    fontSize: '14px'
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <header style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '15px' }}>
        <div style={{ backgroundColor: '#e53e3e', padding: '10px', borderRadius: '12px', color: 'white' }}>
          <Shield size={24} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: '#1a202c' }}>Panneau d'administration</h1>
          <p style={{ margin: 0, color: '#718096' }}>Gestion globale des utilisateurs et des compétitions</p>
        </div>
      </header>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '25px', backgroundColor: '#edf2f7', padding: '5px', borderRadius: '10px', width: 'fit-content' }}>
        <button
          onClick={() => setActiveSubTab('users')}
          style={{
            padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
            backgroundColor: activeSubTab === 'users' ? 'white' : 'transparent',
            fontWeight: activeSubTab === 'users' ? 'bold' : 'normal',
            display: 'flex', alignItems: 'center', gap: '8px',
            boxShadow: activeSubTab === 'users' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          <Users size={18} /> Utilisateurs ({users.length})
        </button>
        <button
          onClick={() => setActiveSubTab('competitions')}
          style={{
            padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
            backgroundColor: activeSubTab === 'competitions' ? 'white' : 'transparent',
            fontWeight: activeSubTab === 'competitions' ? 'bold' : 'normal',
            display: 'flex', alignItems: 'center', gap: '8px',
            boxShadow: activeSubTab === 'competitions' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          <Trophy size={18} /> Compétitions ({competitions.length})
        </button>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#718096' }}>Chargement des données...</div>
        ) : activeSubTab === 'users' ? (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Utilisateur</th>
                <th style={tableHeaderStyle}>Email</th>
                <th style={tableHeaderStyle}>Crédits</th>
                <th style={tableHeaderStyle}>Admin</th>
                <th style={tableHeaderStyle}>Inscription</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: 'bold' }}>{u.first_name} {u.last_name}</div>
                    <div style={{ fontSize: '11px', color: '#a0aec0' }}>ID: {u.id.substring(0, 8)}...</div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Mail size={14} color="#a0aec0" /> {u.email || "Non renseigné"}
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <span style={{ backgroundColor: '#ebf8ff', color: '#2b6cb0', padding: '4px 10px', borderRadius: '20px', fontWeight: 'bold' }}>
                      {u.credits}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    {u.is_admin ? (
                      <span style={{ color: '#38a169', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}><Shield size={14} /> Oui</span>
                    ) : (
                      <span style={{ color: '#cbd5e0' }}>Non</span>
                    )}
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#718096' }}>
                      <Calendar size={14} /> {u.updated_at ? new Date(u.updated_at).toLocaleDateString() : "N/A"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Compétition</th>
                <th style={tableHeaderStyle}>Propriétaire</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={tableHeaderStyle}>Date</th>
                <th style={tableHeaderStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {competitions.map(c => (
                <tr key={c.id} style={{ transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: 'bold' }}>{c.name}</div>
                    <div style={{ fontSize: '12px', color: '#718096' }}>📍 {c.address}</div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: '500' }}>{c.owner?.first_name} {c.owner?.last_name}</div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>{c.owner?.email}</div>
                  </td>
                  <td style={tableCellStyle}>
                    {c.visibility === 'public' ? (
                      <span style={{ color: '#38a169', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 'bold' }}><Globe size={14} /> Public</span>
                    ) : (
                      <span style={{ color: '#718096', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}><Lock size={14} /> Privé</span>
                    )}
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ fontSize: '13px' }}>{new Date(c.event_date).toLocaleDateString()}</div>
                  </td>
                  <td style={tableCellStyle}>
                    <button
                      onClick={() => onSelectCompetition(c)}
                      style={{
                        padding: '6px 12px', backgroundColor: '#3182ce', color: 'white',
                        border: 'none', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px'
                      }}
                    >
                      <ExternalLink size={14} /> Gérer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
